import React from 'react';
import { useTenantContext } from '../../context/TenantContext';
import { IconSelector } from './IconSelector';

interface ColorThemeConfigProps {
  formData: any;
  handleConfigChange: (path: string, value: any) => void;
}

export const ColorThemeConfig: React.FC<ColorThemeConfigProps> = ({ formData, handleConfigChange }) => {
  const { setPreviewValue } = useTenantContext();

  return (
    <div className="space-y-12">
      {/* Section Couleurs du thème */}
      <div>
        <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Couleurs du thème</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 1 <span className="text-gray-500 text-xs">(tenant.config.color1)</span></label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={formData.config.color1 || '#ffffff'}
                onChange={(e) => handleConfigChange('color1', e.target.value)}
                className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={formData.config.color1 || '#ffffff'}
                onChange={(e) => handleConfigChange('color1', e.target.value)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 2 <span className="text-gray-500 text-xs">(tenant.config.color2)</span></label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={formData.config.color2 || '#000000'}
                onChange={(e) => handleConfigChange('color2', e.target.value)}
                className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={formData.config.color2 || '#000000'}
                onChange={(e) => handleConfigChange('color2', e.target.value)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 3 <span className="text-gray-500 text-xs">(tenant.config.color3)</span></label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={formData.config.color3 || '#4b5563'}
                onChange={(e) => handleConfigChange('color3', e.target.value)}
                className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={formData.config.color3 || '#4b5563'}
                onChange={(e) => handleConfigChange('color3', e.target.value)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 4 <span className="text-gray-500 text-xs">(tenant.config.color4)</span></label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={formData.config.color4 || '#9ca3af'}
                onChange={(e) => handleConfigChange('color4', e.target.value)}
                className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={formData.config.color4 || '#9ca3af'}
                onChange={(e) => handleConfigChange('color4', e.target.value)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Header (Barre inférieure orientée boutique) */}
      {(() => {
        const defaultBottombar = {
          enabled: false,
          showOnMobile: true,
          backgroundColor: formData.config.publicHeader?.backgroundColor || '#ffffff',
          textColor: formData.config.publicHeader?.textColor || '#1e1b4b',
          iconColor: formData.config.publicHeader?.iconColor || '#f97316',
          borderTop: true,
          borderColor: '#e5e7eb',
          categories: {
            enabled: true,
            source: 'auto' as 'auto' | 'manual',
            pillStyle: 'filled' as 'filled' | 'outline',
            showCounts: false,
            maxItems: 8,
            manualItems: [
              { id: 'promos', name: 'Promotions', href: '/boutique?categorie=promotions' },
              { id: 'nouveautes', name: 'Nouveautés', href: '/boutique?categorie=nouveautes' },
              { id: 'best', name: 'Meilleures Ventes', href: '/boutique?categorie=best' },
            ],
          },
          actions: {
            showSearch: true,
            searchPlaceholder: 'Rechercher dans la boutique...',
            showContact: true,
            contactHref: '/contact',
          },
        } as const;

        const bottombar = {
          ...defaultBottombar,
          ...formData.config.publicHeader?.bottombar,
          categories: {
            ...defaultBottombar.categories,
            ...formData.config.publicHeader?.bottombar?.categories,
            manualItems: formData.config.publicHeader?.bottombar?.categories?.manualItems || defaultBottombar.categories.manualItems,
          },
          actions: {
            ...defaultBottombar.actions,
            ...formData.config.publicHeader?.bottombar?.actions,
          },
        };

        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-white border-b border-white/10 pb-2">Bottom Header (barre boutique)</h3>
            <p className="text-gray-400 text-sm">Affiche une barre catégories/commande sous le header principal. Idéale pour la boutique (catégories en pills, recherche rapide, contact).</p>

            {/* Activation & affichage */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="bottombar-enabled"
                  checked={formData.config.publicHeader?.bottombar?.enabled ?? bottombar.enabled}
                  onChange={(e) => {
                    handleConfigChange('publicHeader.bottombar.enabled', e.target.checked);
                    setPreviewValue('publicHeader.bottombar.enabled', e.target.checked);
                  }}
                  className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
                />
                <label htmlFor="bottombar-enabled" className="text-gray-200 cursor-pointer">Activer la barre boutique</label>
              </div>
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="bottombar-mobile"
                  checked={formData.config.publicHeader?.bottombar?.showOnMobile ?? bottombar.showOnMobile}
                  onChange={(e) => {
                    handleConfigChange('publicHeader.bottombar.showOnMobile', e.target.checked);
                    setPreviewValue('publicHeader.bottombar.showOnMobile', e.target.checked);
                  }}
                  className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
                />
                <label htmlFor="bottombar-mobile" className="text-gray-200 cursor-pointer">Afficher sur mobile</label>
              </div>
            </div>

            {/* Couleurs & bordure */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de fond</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.config.publicHeader?.bottombar?.backgroundColor || bottombar.backgroundColor}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.backgroundColor', e.target.value);
                      setPreviewValue('publicHeader.bottombar.backgroundColor', e.target.value);
                    }}
                    className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.config.publicHeader?.bottombar?.backgroundColor || bottombar.backgroundColor}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.backgroundColor', e.target.value);
                      setPreviewValue('publicHeader.bottombar.backgroundColor', e.target.value);
                    }}
                    className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Couleur du texte</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.config.publicHeader?.bottombar?.textColor || bottombar.textColor}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.textColor', e.target.value);
                      setPreviewValue('publicHeader.bottombar.textColor', e.target.value);
                    }}
                    className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.config.publicHeader?.bottombar?.textColor || bottombar.textColor}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.textColor', e.target.value);
                      setPreviewValue('publicHeader.bottombar.textColor', e.target.value);
                    }}
                    className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Couleur des icônes</label>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.config.publicHeader?.bottombar?.iconColor || bottombar.iconColor}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.iconColor', e.target.value);
                      setPreviewValue('publicHeader.bottombar.iconColor', e.target.value);
                    }}
                    className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.config.publicHeader?.bottombar?.iconColor || bottombar.iconColor}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.iconColor', e.target.value);
                      setPreviewValue('publicHeader.bottombar.iconColor', e.target.value);
                    }}
                    className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Bordure haute</label>
                <div className="flex items-center space-x-3 mb-2">
                  <input
                    type="checkbox"
                    id="bottombar-border"
                    checked={formData.config.publicHeader?.bottombar?.borderTop ?? bottombar.borderTop}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.borderTop', e.target.checked);
                      setPreviewValue('publicHeader.bottombar.borderTop', e.target.checked);
                    }}
                    className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
                  />
                  <label htmlFor="bottombar-border" className="text-gray-200 cursor-pointer">Afficher la bordure</label>
                </div>
                <div className="flex space-x-2">
                  <input
                    type="color"
                    value={formData.config.publicHeader?.bottombar?.borderColor || bottombar.borderColor}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.borderColor', e.target.value);
                      setPreviewValue('publicHeader.bottombar.borderColor', e.target.value);
                    }}
                    className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.config.publicHeader?.bottombar?.borderColor || bottombar.borderColor}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.borderColor', e.target.value);
                      setPreviewValue('publicHeader.bottombar.borderColor', e.target.value);
                    }}
                    className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>
            </div>

            {/* Catégories */}
            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h4 className="text-md font-semibold text-white mb-4">Catégories</h4>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.config.publicHeader?.bottombar?.categories?.enabled ?? bottombar.categories.enabled}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.categories.enabled', e.target.checked);
                      setPreviewValue('publicHeader.bottombar.categories.enabled', e.target.checked);
                    }}
                    className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
                  />
                  <span className="text-gray-200 text-sm">Afficher les catégories</span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Source</label>
                  <select
                    value={formData.config.publicHeader?.bottombar?.categories?.source || bottombar.categories.source}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.categories.source', e.target.value);
                      setPreviewValue('publicHeader.bottombar.categories.source', e.target.value);
                    }}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="auto" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Automatique (shop)</option>
                    <option value="manual" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Manuelle</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Nombre max</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.config.publicHeader?.bottombar?.categories?.maxItems ?? bottombar.categories.maxItems}
                    onChange={(e) => {
                      const v = Number(e.target.value || 1);
                      handleConfigChange('publicHeader.bottombar.categories.maxItems', v);
                      setPreviewValue('publicHeader.bottombar.categories.maxItems', v);
                    }}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Style des pills</label>
                  <select
                    value={formData.config.publicHeader?.bottombar?.categories?.pillStyle || bottombar.categories.pillStyle}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.categories.pillStyle', e.target.value);
                      setPreviewValue('publicHeader.bottombar.categories.pillStyle', e.target.value);
                    }}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="filled" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Plein</option>
                    <option value="outline" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Contour</option>
                  </select>
                </div>
              </div>

              {/* Éléments manuels */}
              {((formData.config.publicHeader?.bottombar?.categories?.source || bottombar.categories.source) === 'manual') && (
                <div className="mt-4 space-y-3">
                  {(bottombar.categories.manualItems || []).map((it: any, idx: number) => (
                    <div key={it.id || idx} className="grid grid-cols-1 md:grid-cols-10 gap-3 items-center">
                      <div className="md:col-span-3">
                        <label className="block text-xs text-gray-400 mb-1">Nom</label>
                        <input
                          type="text"
                          value={(formData.config.publicHeader?.bottombar?.categories?.manualItems?.[idx]?.name) ?? it.name}
                          onChange={(e) => {
                            const list = [...(formData.config.publicHeader?.bottombar?.categories?.manualItems || bottombar.categories.manualItems)];
                            list[idx] = { ...(list[idx] || it), name: e.target.value };
                            handleConfigChange('publicHeader.bottombar.categories.manualItems', list);
                            setPreviewValue('publicHeader.bottombar.categories.manualItems', list);
                          }}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                        />
                      </div>
                      <div className="md:col-span-6">
                        <label className="block text-xs text-gray-400 mb-1">Lien</label>
                        <input
                          type="text"
                          value={(formData.config.publicHeader?.bottombar?.categories?.manualItems?.[idx]?.href) ?? it.href}
                          onChange={(e) => {
                            const list = [...(formData.config.publicHeader?.bottombar?.categories?.manualItems || bottombar.categories.manualItems)];
                            list[idx] = { ...(list[idx] || it), href: e.target.value };
                            handleConfigChange('publicHeader.bottombar.categories.manualItems', list);
                            setPreviewValue('publicHeader.bottombar.categories.manualItems', list);
                          }}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                        />
                      </div>
                      <div className="md:col-span-1 flex items-end">
                        <button
                          type="button"
                          onClick={() => {
                            const base = (formData.config.publicHeader?.bottombar?.categories?.manualItems || bottombar.categories.manualItems);
                            const list = base.filter((_: any, i: number) => i !== idx);
                            handleConfigChange('publicHeader.bottombar.categories.manualItems', list);
                            setPreviewValue('publicHeader.bottombar.categories.manualItems', list);
                          }}
                          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md border border-red-500/30"
                        >
                          Supprimer
                        </button>
                      </div>
                    </div>
                  ))}
                  <div>
                    <button
                      type="button"
                      onClick={() => {
                        const list = [...(formData.config.publicHeader?.bottombar?.categories?.manualItems || bottombar.categories.manualItems)];
                        list.push({ id: `manual_${Date.now()}`, name: 'Nouvelle catégorie', href: '/boutique' });
                        handleConfigChange('publicHeader.bottombar.categories.manualItems', list);
                        setPreviewValue('publicHeader.bottombar.categories.manualItems', list);
                      }}
                      className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10"
                    >
                      Ajouter une catégorie
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Actions (Recherche, Contact) */}
            <div className="bg-white/5 rounded-lg p-6 border border-white/10">
              <h4 className="text-md font-semibold text-white mb-4">Actions</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.config.publicHeader?.bottombar?.actions?.showSearch ?? bottombar.actions.showSearch}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.actions.showSearch', e.target.checked);
                      setPreviewValue('publicHeader.bottombar.actions.showSearch', e.target.checked);
                    }}
                    className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
                  />
                  <span className="text-gray-200 text-sm">Afficher la recherche</span>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Placeholder de recherche</label>
                  <input
                    type="text"
                    value={formData.config.publicHeader?.bottombar?.actions?.searchPlaceholder || bottombar.actions.searchPlaceholder}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.actions.searchPlaceholder', e.target.value);
                      setPreviewValue('publicHeader.bottombar.actions.searchPlaceholder', e.target.value);
                    }}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={formData.config.publicHeader?.bottombar?.actions?.showContact ?? bottombar.actions.showContact}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.actions.showContact', e.target.checked);
                      setPreviewValue('publicHeader.bottombar.actions.showContact', e.target.checked);
                    }}
                    className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
                  />
                  <span className="text-gray-200 text-sm">Afficher le lien Contact</span>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-300 mb-2">Lien Contact</label>
                  <input
                    type="text"
                    value={formData.config.publicHeader?.bottombar?.actions?.contactHref || bottombar.actions.contactHref}
                    onChange={(e) => {
                      handleConfigChange('publicHeader.bottombar.actions.contactHref', e.target.value);
                      setPreviewValue('publicHeader.bottombar.actions.contactHref', e.target.value);
                    }}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Section NotFound: Fond & Icônes flottantes */}
      <div>
        <h3 className="text-lg font-medium text-white mb-6 border-b border-white/10 pb-2">Pages - Fond et icônes</h3>

        {/* Fond (overlay au-dessus de l'image) */}
        <div className="bg-white/5 rounded-lg p-6 border border-white/10 mb-6">
          <h4 className="text-base font-semibold text-white mb-4">Fond (overlay au-dessus de l'image)</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Dégradé activé <span className="text-gray-500 text-xs">(tenant.config.theme.notFound.overlay.useGradient)</span></label>
              <div className="flex items-center space-x-3 mt-1">
                <input
                  type="checkbox"
                  checked={!!formData.config.theme?.notFound?.overlay?.useGradient}
                  onChange={(e) => {
                    handleConfigChange('theme.notFound.overlay', {
                      ...(formData.config.theme?.notFound?.overlay || {}),
                      useGradient: e.target.checked,
                      enabled: true,
                    });
                    setPreviewValue('theme.notFound.overlay', {
                      ...(formData.config.theme?.notFound?.overlay || {}),
                      useGradient: e.target.checked,
                      enabled: true,
                    });
                  }}
                  className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
                />
                <span className="text-gray-200">Utiliser un dégradé</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Orientation du dégradé <span className="text-gray-500 text-xs">(tenant.config.theme.notFound.overlay.orientation)</span></label>
              <select
                value={formData.config.theme?.notFound?.overlay?.orientation || 'to-b'}
                onChange={(e) => {
                  const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), orientation: e.target.value, enabled: true };
                  handleConfigChange('theme.notFound.overlay', overlay);
                  setPreviewValue('theme.notFound.overlay', overlay);
                }}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                style={{ colorScheme: 'dark' }}
              >
                <option value="to-b" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Bas (to-b)</option>
                <option value="to-t" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Haut (to-t)</option>
                <option value="to-r" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Droite (to-r)</option>
                <option value="to-l" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Gauche (to-l)</option>
                <option value="to-tr" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Haut-Droite (to-tr)</option>
                <option value="to-br" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Bas-Droite (to-br)</option>
                <option value="to-tl" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Haut-Gauche (to-tl)</option>
                <option value="to-bl" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Bas-Gauche (to-bl)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 1 & Opacité <span className="text-gray-500 text-xs">(overlay.color1, overlay.opacity1)</span></label>
              <div className="flex space-x-2">
                <div className="relative">
                  <input
                    type="color"
                    value={formData.config.theme?.notFound?.overlay?.color1 || (formData.config.color2 || '#08062b')}
                    onChange={(e) => {
                      const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), color1: e.target.value, enabled: true };
                      handleConfigChange('theme.notFound.overlay', overlay);
                      setPreviewValue('theme.notFound.overlay', overlay);
                    }}
                    className="h-10 w-10 rounded border border-white/10 cursor-pointer"
                    style={{ backgroundColor: formData.config.theme?.notFound?.overlay?.color1 || (formData.config.color2 || '#08062b') }}
                  />
                </div>
                <input
                  type="text"
                  value={formData.config.theme?.notFound?.overlay?.color1 || (formData.config.color2 || '#08062b')}
                  onChange={(e) => {
                    const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), color1: e.target.value, enabled: true };
                    handleConfigChange('theme.notFound.overlay', overlay);
                    setPreviewValue('theme.notFound.overlay', overlay);
                  }}
                  className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={(formData.config.theme?.notFound?.overlay?.opacity1 ?? 0.95)}
                  onChange={(e) => {
                    const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), opacity1: parseFloat(e.target.value), enabled: true };
                    handleConfigChange('theme.notFound.overlay', overlay);
                    setPreviewValue('theme.notFound.overlay', overlay);
                  }}
                  className="w-16 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 2 & Opacité <span className="text-gray-500 text-xs">(overlay.color2, overlay.opacity2)</span></label>
              <div className="flex space-x-2">
                <div className="relative">
                  <input
                    type="color"
                    value={formData.config.theme?.notFound?.overlay?.color2 || (formData.config.color2 || '#08062b')}
                    onChange={(e) => {
                      const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), color2: e.target.value, enabled: true };
                      handleConfigChange('theme.notFound.overlay', overlay);
                      setPreviewValue('theme.notFound.overlay', overlay);
                    }}
                    className="h-10 w-10 rounded border border-white/10 cursor-pointer"
                    style={{ backgroundColor: formData.config.theme?.notFound?.overlay?.color2 || (formData.config.color2 || '#08062b') }}
                  />
                </div>
                <input
                  type="text"
                  value={formData.config.theme?.notFound?.overlay?.color2 || (formData.config.color2 || '#08062b')}
                  onChange={(e) => {
                    const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), color2: e.target.value, enabled: true };
                    handleConfigChange('theme.notFound.overlay', overlay);
                    setPreviewValue('theme.notFound.overlay', overlay);
                  }}
                  className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={(formData.config.theme?.notFound?.overlay?.opacity2 ?? 0.80)}
                  onChange={(e) => {
                    const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), opacity2: parseFloat(e.target.value), enabled: true };
                    handleConfigChange('theme.notFound.overlay', overlay);
                    setPreviewValue('theme.notFound.overlay', overlay);
                  }}
                  className="w-16 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 3 & Opacité <span className="text-gray-500 text-xs">(overlay.color3, overlay.opacity3)</span></label>
              <div className="flex space-x-2">
                <div className="relative">
                  <input
                    type="color"
                    value={formData.config.theme?.notFound?.overlay?.color3 || (formData.config.color2 || '#08062b')}
                    onChange={(e) => {
                      const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), color3: e.target.value, enabled: true };
                      handleConfigChange('theme.notFound.overlay', overlay);
                      setPreviewValue('theme.notFound.overlay', overlay);
                    }}
                    className="h-10 w-10 rounded border border-white/10 cursor-pointer"
                    style={{ backgroundColor: formData.config.theme?.notFound?.overlay?.color3 || (formData.config.color2 || '#08062b') }}
                  />
                </div>
                <input
                  type="text"
                  value={formData.config.theme?.notFound?.overlay?.color3 || (formData.config.color2 || '#08062b')}
                  onChange={(e) => {
                    const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), color3: e.target.value, enabled: true };
                    handleConfigChange('theme.notFound.overlay', overlay);
                    setPreviewValue('theme.notFound.overlay', overlay);
                  }}
                  className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={(formData.config.theme?.notFound?.overlay?.opacity3 ?? 0.95)}
                  onChange={(e) => {
                    const overlay = { ...(formData.config.theme?.notFound?.overlay || {}), opacity3: parseFloat(e.target.value), enabled: true };
                    handleConfigChange('theme.notFound.overlay', overlay);
                    setPreviewValue('theme.notFound.overlay', overlay);
                  }}
                  className="w-16 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Icônes flottantes (4 positions) */}
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h4 className="text-base font-semibold text-white mb-4">Icônes flottantes (4 positions)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[0,1,2,3].map((idx) => {
              const defaults = [
                { enabled: true, icon: 'Wrench', color: formData.config.color1 || '#fe8d17', size: 32 },
                { enabled: true, icon: 'Server', color: formData.config.color2 || '#008aef', size: 28 },
                { enabled: true, icon: 'Code', color: formData.config.color2 || '#008aef', size: 24 },
                { enabled: true, icon: 'HardDrive', color: formData.config.color1 || '#fe8d17', size: 30 },
              ];
              const icons = (formData.config.theme?.notFound?.icons && Array.isArray(formData.config.theme.notFound.icons) && formData.config.theme.notFound.icons.length === 4)
                ? formData.config.theme.notFound.icons
                : defaults;
              const current = icons[idx] || defaults[idx];
              const labels = ['Haut-Gauche','Haut-Droite','Bas-Gauche','Bas-Droite'];
              return (
                <div key={idx} className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-300 font-medium">{labels[idx]}</span>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={!!current.enabled}
                        onChange={(e) => {
                          const newIcons = [...icons];
                          newIcons[idx] = { ...current, enabled: e.target.checked };
                          handleConfigChange('theme.notFound.icons', newIcons);
                          setPreviewValue('theme.notFound.icons', newIcons);
                        }}
                        className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
                      />
                      <span className="text-gray-200 text-sm">Activer</span>
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Icône</label>
                    <IconSelector
                      value={current.icon}
                      onChange={(value) => {
                        const newIcons = [...icons];
                        newIcons[idx] = { ...current, icon: value };
                        handleConfigChange('theme.notFound.icons', newIcons);
                        setPreviewValue('theme.notFound.icons', newIcons);
                      }}
                      preset="all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Couleur</label>
                      <div className="flex space-x-2">
                        <div className="relative">
                          <input
                            type="color"
                            value={current.color}
                            onChange={(e) => {
                              const newIcons = [...icons];
                              newIcons[idx] = { ...current, color: e.target.value };
                              handleConfigChange('theme.notFound.icons', newIcons);
                              setPreviewValue('theme.notFound.icons', newIcons);
                            }}
                            className="h-10 w-10 rounded border border-white/10 cursor-pointer"
                            style={{ backgroundColor: current.color }}
                          />
                        </div>
                        <input
                          type="text"
                          value={current.color}
                          onChange={(e) => {
                            const newIcons = [...icons];
                            newIcons[idx] = { ...current, color: e.target.value };
                            handleConfigChange('theme.notFound.icons', newIcons);
                            setPreviewValue('theme.notFound.icons', newIcons);
                          }}
                          className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Taille (px)</label>
                      <input
                        type="number"
                        min={12}
                        max={64}
                        step={2}
                        value={current.size}
                        onChange={(e) => {
                          const newIcons = [...icons];
                          newIcons[idx] = { ...current, size: parseInt(e.target.value || '0', 10) };
                          handleConfigChange('theme.notFound.icons', newIcons);
                          setPreviewValue('theme.notFound.icons', newIcons);
                        }}
                        className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColorThemeConfig;
